/**
 * checkoutError - the ONE error boundary for the F9 card-checkout data layer.
 *
 * Task: T-P5-D17-F9 (T-F9-3)
 *
 * Replaces the retired mock-payment hook's private `mapPaymentError`. All three
 * checkout hooks — useCreatePaymentIntent, usePaymentStatus,
 * useManualConfirm — funnel transport errors through `mapCheckoutError`, so the
 * page work (T-F9-4…7) branches on ONE stable `{ message, code, canRetry }`
 * shape.
 *
 * `code` is the RAW server error code, preserved verbatim — the page routes on
 * it (e.g. PAYMENT_COMPLETED_CONFLICT → conflict handler, STALE_CHECKOUT →
 * carrier purge + reset, CARD_UNAVAILABLE/VENMO_UNAVAILABLE → refetch + reroute).
 * `message` is user-facing copy; `canRetry` says whether the SAME action can be
 * retried in place.
 */

/**
 * User-facing checkout error. Decouples the UI from ApiError transport details.
 */
export interface CheckoutError {
  /** User-facing message. */
  message: string;
  /** Raw server error code (verbatim) — the page branches on this. */
  code: string;
  /** Whether the same action can be retried in place. */
  canRetry: boolean;
}

const GENERIC_RETRYABLE = 'Something went wrong. Please try again.';

/**
 * Map an API/network error to a CheckoutError.
 *
 * Covers every code the F9 hooks route (intent create §4.2, status poll §5,
 * manual confirm §6) plus status-based fallbacks (401/404/429/5xx) and network
 * failures.
 */
/**
 * An HTTP error from the app's client (ApiError) or the studio website's: both are Errors with a
 * numeric status, code and message. Read by shape, so this module imports no app transport
 * (shared-client-screens D2).
 */
function isHttpError(error: unknown): error is { status: number; code?: string; message: string } {
  return error instanceof Error && typeof (error as { status?: unknown }).status === 'number';
}

export function mapCheckoutError(error: unknown): CheckoutError {
  if (isHttpError(error)) {
    const code = error.code ?? '';

    switch (code) {
      // --- card-checkout (intent create §4.2) ---
      case 'CARD_UNAVAILABLE':
        return { message: 'Card payment is paused right now.', code, canRetry: false };
      case 'PAYMENT_INTENT_IN_PROGRESS':
        return { message: 'Finishing up your previous attempt…', code, canRetry: true };
      case 'PAYMENT_WINDOW_TOO_CLOSE':
        return {
          message: "There isn't enough time left to pay for this slot. Please pick a new time.",
          code,
          canRetry: false,
        };

      // --- shared conflict + settling (no-blind-poll handlers own these) ---
      case 'PAYMENT_COMPLETED_CONFLICT':
        return { message: 'This booking was already paid.', code, canRetry: false };
      case 'PAYMENT_SETTLING':
        return { message: 'Your card payment is still processing, one moment.', code, canRetry: false };
      // studio-shop D6: the order's one payment, paid or being paid (a second tab).
      case 'ORDER_PAID':
        return { message: 'This order is already paid.', code, canRetry: false };
      case 'PAYMENT_IN_PROGRESS':
        return { message: 'Your payment is being processed.', code, canRetry: false };

      // --- status poll (§5) ---
      case 'STALE_CHECKOUT':
        return { message: 'This checkout is no longer current.', code, canRetry: false };

      // --- manual confirm (§6) ---
      case 'VENMO_UNAVAILABLE':
        return { message: "Venmo isn't available for this booking right now.", code, canRetry: false };
      case 'REFERENCE_GENERATION_FAILED':
        return {
          message: "Couldn't generate your payment reference. Please try again.",
          code,
          canRetry: true,
        };

      // --- reservation / status / session (shared) ---
      case 'HOLD_EXPIRED':
      case 'NO_PLACEHOLDER':
      case 'NO_SLOT_SELECTED':
        return { message: 'Your time slot hold expired. Please pick a new time.', code, canRetry: false };
      case 'INVALID_STATUS':
        return { message: 'This booking is not awaiting payment.', code, canRetry: false };
      case 'FLOW_NOT_FOUND':
      case 'BOOKING_NOT_FOUND':
        return { message: 'Session expired. Please start over from Telegram.', code, canRetry: false };
      case 'ACCESS_DENIED':
      case 'SESSION_FLOW_MISMATCH':
      case 'FLOW_SESSION_REQUIRED':
      case 'SESSION_EXPIRED':
        return { message: 'Session error. Please start over from Telegram.', code, canRetry: false };
    }

    // Status-based fallbacks (after explicit codes).
    if (error.status === 401) {
      return { message: 'Session expired. Please start over from Telegram.', code: code || 'SESSION_EXPIRED', canRetry: false };
    }
    if (error.status === 404) {
      return { message: 'Session expired. Please start over from Telegram.', code: code || 'NOT_FOUND', canRetry: false };
    }
    if (error.status === 429 || code === 'RATE_LIMIT_EXCEEDED') {
      return {
        message: 'Too many attempts. Please wait a moment and try again.',
        code: code || 'RATE_LIMIT_EXCEEDED',
        canRetry: true,
      };
    }
    if (
      error.status >= 500 ||
      code === 'SYSTEM_ERROR' ||
      code === 'SERVICE_TEMPORARILY_UNAVAILABLE' ||
      code === 'PAYMENT_PROVIDER_UNAVAILABLE'
    ) {
      return { message: GENERIC_RETRYABLE, code: code || 'SERVER_ERROR', canRetry: true };
    }

    // Other API errors — surface the server message, non-retryable.
    return { message: error.message || GENERIC_RETRYABLE, code: code || 'API_ERROR', canRetry: false };
  }

  // Network errors (fetch failed entirely).
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return { message: 'Connection error. Please check your network and try again.', code: 'NETWORK_ERROR', canRetry: true };
  }

  return { message: GENERIC_RETRYABLE, code: 'UNKNOWN_ERROR', canRetry: true };
}
